import ViSearch, {
  type ProductSearchResponse,
  type ProductSearchResponseError,
} from 'visearch-javascript-sdk'
import type { VisenzeHookType } from '../components/VisenzeRecommendations.props'
/**
 * Composable for interacting with Visenze product recommendation services.
 *
 * @description Provides methods to fetch product recommendations based on SKU.
 * @returns {Object} An object with methods for fetching product recommendations.
 */
export const useVisenze = () => {
  /** Retrieve Visenze configuration from app config */
  const { currentVisenzeConfig, currentLanguage, currentCountry } =
    useAppConfig()
  const dataLang = computed(
    () => `${currentLanguage}_${(currentCountry as string).toUpperCase()}`
  )

  /**
   * List of shared attributes to retrieve for product recommendations
   * @type {string[]}
   */
  const sharedAttrs = [
    'product_url',
    'size_list',
    'title',
    'brand',
    'additional_image_url',
    'price_amount',
    'tags',
    'alternative_color',
    'color',
    'price',
  ]

  /**
   * Handles errors in Visenze product search responses.
   *
   * @param {ProductSearchResponseError} error - The error returned by Visenze.
   * @description Logs errors to the console for debugging purposes.
   */
  const onErrorHandler = (error: ProductSearchResponseError) => {
    console.log(error)
  }

  /**
   * Fetches "Complete the Look" product recommendations for a given SKU.
   *
   * @param {string} sku - The SKU of the product to find recommendations for.
   * @returns {Promise<VisenzeHookType>} An array of recommended products.
   * @description
   * - Uses Visenze API to fetch products that complete a look
   * - Requires Visenze app key and placement ID
   * - Limits recommendations to 3 products
   * - Returns an empty array if no recommendations are found
   */
  const completeTheLook = async (sku: string): Promise<VisenzeHookType> => {
    const data: VisenzeHookType = {
      products: [],
    }

    if (currentVisenzeConfig?.app_key && currentVisenzeConfig.pdp_stl) {
      const visearch = ViSearch({
        app_key: currentVisenzeConfig?.app_key,
        placement_id: currentVisenzeConfig.pdp_stl,
      })
      const onResponse = (response: ProductSearchResponse) => {
        if (response.status === 'OK' && Array.isArray(response.result)) {
          data.products = response.result
          data.bestImage = response.product_info?.best_images?.find(
            item => item.type === 'outfit'
          )?.url
        }
      }

      await visearch.productRecommendations(
        sku,
        {
          attrs_to_get: sharedAttrs,
          limit: 3,
          return_product_info: true,
          show_best_product_images: true,
          locale: dataLang.value,
        },
        onResponse,
        onErrorHandler
      )
    }

    return data as VisenzeHookType
  }

  /**
   * Fetches similar product recommendations for a given SKU.
   *
   * @param {string} sku - The SKU of the product to find similar products for.
   * @returns {Promise<VisenzeHookType>} An array of similar products.
   * @description
   * - Uses Visenze API to fetch products similar to the given SKU
   * - Requires Visenze app key and placement ID
   * - Returns an empty array if no similar products are found
   */
  const similarProducts = async (sku: string): Promise<VisenzeHookType> => {
    const data: VisenzeHookType = {
      products: [],
    }

    if (currentVisenzeConfig?.app_key && currentVisenzeConfig?.pdp_vsr) {
      const visearch = ViSearch({
        app_key: currentVisenzeConfig?.app_key,
        placement_id: currentVisenzeConfig.pdp_vsr,
      })

      const onResponse = (response: ProductSearchResponse) => {
        if (response.status === 'OK' && Array.isArray(response.result)) {
          data.products = response.result
        }
      }

      await visearch.productRecommendations(
        sku,
        {
          attrs_to_get: sharedAttrs,
          locale: dataLang.value,
        },
        onResponse,
        onErrorHandler
      )
    }

    return data
  }

  return {
    completeTheLook,
    similarProducts,
  }
}
